import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AutoReviewState, CalculationStatusResponse, CalculationStatusResponseV2, CalculationStepStatus } from '../../core/Types/Calculation.types';
import { Guid } from 'guid-typescript';
import { fetchMostRecentCalculationRuns, lastCalculationRun } from '../../api/calculation/calculationController-api';
import { AppDispatch, RootState } from '../../app/store';
import { checkCalculationInProgress } from '../../core/Utils/utils';
import StatusCode from 'status-code-enum';
import { handleGetStatusDetail } from '../status-widget/statusWidget.slice';

type State = {
	isCalculationHistoryDialogVisible: boolean;
	mostRecentCalcRun: CalculationStatusResponse;
	latestSuccessfulCalcRun?: CalculationStatusResponse;
	latestFailedCalcRun: CalculationStatusResponse;
	isCalculationInProgress: boolean;
	isImportEnabled: boolean;
	isCancelEnabled: boolean;
	activeCalculationId: Guid;
	autoReviewComplete: boolean;
	autoReviewUrl: string;
	isTestCaseRunInProgress?: boolean;
};

const initialState: State = {
	isCalculationHistoryDialogVisible: false,
	mostRecentCalcRun: {} as CalculationStatusResponse,
	latestFailedCalcRun: {} as CalculationStatusResponse,
	isCalculationInProgress: false,
	isImportEnabled: true,
	isTestCaseRunInProgress: false,
	isCancelEnabled: false,
	activeCalculationId: Guid.createEmpty(),
	autoReviewComplete: false,
	autoReviewUrl: ''
};

export const handleSetRecentCompletedCalcRunState = createAsyncThunk<
	void,
	Guid,
	{
		dispatch: AppDispatch;
		state: RootState;
	}
>('calculation/setMostRecentCalcRunState', async (jobId, thunkAPI) => {
	const recentCalculationRunResponse = await fetchMostRecentCalculationRuns(jobId);
	const response = JSON.parse(recentCalculationRunResponse.Data.toString()) as CalculationStatusResponseV2;
	thunkAPI.dispatch(setCalculationInProgress(false));
	thunkAPI.dispatch(completedCalculationRunDetails(response));
	thunkAPI.dispatch(handleGetStatusDetail(jobId));
});

export const setRecentCalcRunState = createAsyncThunk<
	void,
	Guid,
	{
		dispatch: AppDispatch;
		state: RootState;
	}
>('calculation/setRecentCalcRunState', async (jobId, thunkAPI) => {
	const recentCalculationRunResponse = await fetchMostRecentCalculationRuns(jobId);
	const response = JSON.parse(recentCalculationRunResponse.Data.toString()) as CalculationStatusResponseV2;
	thunkAPI.dispatch(completedCalculationRunDetails(response));
});

export const handleSetRecentCalcRunState = createAsyncThunk<
	void,
	Guid,
	{
		dispatch: AppDispatch;
		state: RootState;
	}
>('calculation/setMostRecentCalcRunState', async (jobId, thunkAPI) => {
	const recentCalculationRun = await lastCalculationRun(jobId);
	if (recentCalculationRun.Success && recentCalculationRun.StatusCode === StatusCode.SuccessNoContent) {
		thunkAPI.dispatch(setCalculationInProgress(false));
		return;
	}
	if (recentCalculationRun.Success && recentCalculationRun.StatusCode === StatusCode.SuccessOK) {
		const lastCalcRun = JSON.parse(recentCalculationRun.Data.toString()) as CalculationStatusResponse;
		var inProgressStatus = checkCalculationInProgress(lastCalcRun.status);
		if(inProgressStatus){
			thunkAPI.dispatch(setActiveCalculationId(lastCalcRun.calculationId as Guid));
		}
		if(lastCalcRun.status as CalculationStepStatus === CalculationStepStatus.CancelEnabled){
			thunkAPI.dispatch(setCancelEnabled(true));
		}
		else{
			thunkAPI.dispatch(setCancelEnabled(false));
		}
		thunkAPI.dispatch(setCalculationInProgress(inProgressStatus));
		return;
	}
	thunkAPI.dispatch(setCalculationInProgress(false));
});

const calculationRunSlice = createSlice({
	name: 'calculationRun',
	initialState,
	reducers: {
		calculationHistoryDialogOpened(state): State {
			return {
				...state,
				isCalculationHistoryDialogVisible : true,
				activeCalculationId: state.activeCalculationId as Guid,
				mostRecentCalcRun: state.mostRecentCalcRun as CalculationStatusResponse,
				latestSuccessfulCalcRun: state.latestSuccessfulCalcRun as CalculationStatusResponse,
				latestFailedCalcRun: state.latestFailedCalcRun as CalculationStatusResponse,
			};
		},
		calculationHistoryDialogClosed(state): State {
			return {
				...state,
				isCalculationHistoryDialogVisible : false,
				activeCalculationId: state.activeCalculationId as Guid,
				mostRecentCalcRun: state.mostRecentCalcRun as CalculationStatusResponse,
				latestSuccessfulCalcRun: state.latestSuccessfulCalcRun as CalculationStatusResponse,
				latestFailedCalcRun: state.latestFailedCalcRun as CalculationStatusResponse,
			};
		},
		completedCalculationRunDetails(state, action: PayloadAction<CalculationStatusResponseV2>): State {
			const { lastFailedCalculationRun, lastSuccessfulCalculationRun, mostRecentCalculationRun } = action.payload;
			return {
				...state,
				activeCalculationId: state.activeCalculationId as Guid,
				mostRecentCalcRun: mostRecentCalculationRun,
				latestSuccessfulCalcRun: lastSuccessfulCalculationRun,
				latestFailedCalcRun: lastFailedCalculationRun,
			};
		},
		setCalculationInProgress(state, action: PayloadAction<boolean>): State {
			return {
				...state,
				activeCalculationId: state.activeCalculationId as Guid,
				isCalculationInProgress: action.payload,
				mostRecentCalcRun: state.mostRecentCalcRun as CalculationStatusResponse,
				latestSuccessfulCalcRun: state.latestSuccessfulCalcRun as CalculationStatusResponse,
				latestFailedCalcRun: state.latestFailedCalcRun as CalculationStatusResponse,
			};
		},
		setImportEnabled(state, action: PayloadAction<boolean>) {
			state.isImportEnabled = action.payload;
		},
		setCancelEnabled(state, action: PayloadAction<boolean>) {
			return {
				...state,
				isCancelEnabled: action.payload
			};
		},
		resetLastCalculationRunDetails(): State {
			return {
				...initialState,
			};
		},
		setAutoReviewState(state, action: PayloadAction<AutoReviewState>) {
			return {
			...state,
				autoReviewComplete: action.payload.isSuccess,
				autoReviewUrl: action.payload.autoReviewUrl,
			};
		},
		setActiveCalculationId(state, action: PayloadAction<Guid>): State {
			return {
				...state,
				activeCalculationId: action.payload,
				mostRecentCalcRun: state.mostRecentCalcRun as CalculationStatusResponse,
				latestSuccessfulCalcRun: state.latestSuccessfulCalcRun as CalculationStatusResponse,
				latestFailedCalcRun: state.latestFailedCalcRun as CalculationStatusResponse,
			};
		},
		resetActiveCalculationId(state): State {
			return {
				...state,
				activeCalculationId: Guid.createEmpty(),
				mostRecentCalcRun: state.mostRecentCalcRun as CalculationStatusResponse,
				latestSuccessfulCalcRun: state.latestSuccessfulCalcRun as CalculationStatusResponse,
				latestFailedCalcRun: state.latestFailedCalcRun as CalculationStatusResponse,
			};
		},
		setIsTestCaseRunInProgress(state, action: PayloadAction<boolean>) {
			state.isTestCaseRunInProgress = action.payload;
		}
	},
});

export const {
	calculationHistoryDialogOpened,
	calculationHistoryDialogClosed,
	completedCalculationRunDetails,
	setCalculationInProgress,
	setImportEnabled,
	resetLastCalculationRunDetails,
	setAutoReviewState,
	setCancelEnabled,
	setActiveCalculationId,
	resetActiveCalculationId,
	setIsTestCaseRunInProgress
} = calculationRunSlice.actions;

export default calculationRunSlice.reducer;
